import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context';
import { Input, Card } from '../../components/common';
import { Link, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import AppButton from '../../components/common/AppButton';
import { featureFlags } from '../../config';
import { paths } from '../../routing/paths';
import Button from '@mui/material/Button';
import AuthLayout from '../../components/layout/AuthLayout';

const Login: React.FC = () => {
  const { login, loginWithPassword, completeLogin, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [completingLogin, setCompletingLogin] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    if (!user) {
      return;
    }

    navigate(paths.home);
  }, [user, navigate]);

  useEffect(() => {
    const emailLink = window.location.href;
    const savedEmail = window.localStorage.getItem('emailForSignIn');

    if (savedEmail && emailLink.includes('apiKey=')) {
      setCompletingLogin(true);
      completeLogin(savedEmail, emailLink).catch((err) => {
        setError(err.message);
        setCompletingLogin(false);
      });
    }
  }, [completeLogin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    if (showPassword && !password) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (showPassword) {
        await loginWithPassword(email, password);
      } else {
        await login(email);
        setEmailSent(true);
      }
    } catch (err) {
      if (featureFlags.passwordLogin) {
        setShowPassword(true);
      } else {
        setError('Failed to login. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Card
        sx={{
          maxWidth: 400,
          width: '100%',
          p: 4,
          borderRadius: '8px',
          backgroundColor: 'white',
          boxShadow: 'none',
        }}
        elevation={0}
      >
        <Typography variant="h5" align="center" sx={{ fontWeight: 600, mb: 2 }}>
          Welcome back to{' '}
          <Box component={'span'} sx={(theme) => ({ color: theme.palette.primary.main })}>
            ECGO!
          </Box>
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {emailSent ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            A login link has been sent to your email address. Please check your inbox.
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            {!completingLogin && (
              <Input
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            )}
            {showPassword && featureFlags.passwordLogin && (
              <Input
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ marginTop: 16 }}
              />
            )}
            <AppButton
              type="submit"
              isFullWidth
              isLoading={loading || completingLogin}
              style={{ marginTop: 16 }}
            >
              Login
            </AppButton>
            {showPassword && featureFlags.passwordLogin && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  component={Link}
                  to={paths.forgotPassword}
                  variant="text"
                  sx={{ textTransform: 'none', width: '100%' }}
                >
                  Forgot Password?
                </Button>
              </Box>
            )}
          </form>
        )}
        {!completingLogin && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              Don't have an account yet?
              <Link to={paths.signup} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button
                  variant="text"
                  sx={{ textTransform: 'none', textDecoration: 'underline', ml: 0.5 }}
                >
                  Create account
                </Button>
              </Link>
            </Typography>
          </Box>
        )}
      </Card>
    </AuthLayout>
  );
};

export default Login;
